import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getPasskeyErrorMessage } from '../utils/passkeyErrors';
import './Auth.css';

const SHOW_GOOGLE_SIGNIN = false; // set to true to show "Continue with Google"

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasskeyStep, setShowPasskeyStep] = useState(false);
  const [passkeyAdding, setPasskeyAdding] = useState(false);
  const [passkeyError, setPasskeyError] = useState('');
  const { register, user } = useAuth();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as any)?.returnTo || '/plans';
  const queryParams = new URLSearchParams(location.search);
  const referralFromUrl = queryParams.get('ref') || '';

  useEffect(() => {
    if (user && !showPasskeyStep) {
      navigate(returnTo);
    }
  }, [user, showPasskeyStep, navigate, returnTo]);

  useEffect(() => {
    if (referralFromUrl) {
      setReferralCode(referralFromUrl);
    }
  }, [referralFromUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(
        email,
        password,
        name,
        referralCode ? { referralCode } : undefined
      );
      setShowPasskeyStep(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPasskey = () => {
    navigate(returnTo, { state: { verificationPending: true } });
  };

  const handleAddPasskey = async () => {
    setPasskeyError('');
    setPasskeyAdding(true);
    try {
      const optsRes = await api.post('/auth/passkey/register/options');
      const { options, passkeyToken } = optsRes.data;
      const response = await startRegistration({ optionsJSON: options });
      await api.post('/auth/passkey/register/verify', { passkeyToken, response });
      navigate(returnTo, { state: { verificationPending: true } });
    } catch (err: any) {
      setPasskeyError(err.response?.data?.error || getPasskeyErrorMessage(err, 'register'));
    } finally {
      setPasskeyAdding(false);
    }
  };

  if (showPasskeyStep) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h1>You’re in</h1>
          <p className="auth-subtitle">Check your email to verify your account.</p>
          <div className="passkey-prompt-box" style={{ marginBottom: 20 }}>
            <p style={{ marginBottom: 12 }}>
              Add a passkey now for faster, more secure sign-in next time? (Face ID, Touch ID, or device PIN)
            </p>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Optional — you can always add one later from your dashboard.
            </p>
            {passkeyError && <div className="error" style={{ marginBottom: 12 }}>{passkeyError}</div>}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSkipPasskey}
                disabled={passkeyAdding}
              >
                Skip for now
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddPasskey}
                disabled={passkeyAdding}
              >
                {passkeyAdding ? 'Adding passkey…' : 'Add passkey'}
              </button>
            </div>
          </div>
          <p className="auth-footer" style={{ marginTop: 24 }}>
            <Link to={returnTo} onClick={(e) => { e.preventDefault(); handleSkipPasskey(); }}>
              Continue to Club Kazi
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Join Club Kazi</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                title={showPassword ? 'Hide password' : 'Show password'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Referral Code (Optional)</label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.trim())}
              placeholder="Enter referral code"
            />
          </div>
          {error && <div className="error">{error}</div>}
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
        {SHOW_GOOGLE_SIGNIN && (
          <button
            type="button"
            className="btn btn-secondary auth-social-btn"
            onClick={() => {
              window.location.href = `${apiBaseUrl}/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
            }}
          >
            Continue with Google
          </button>
        )}
        <p className="auth-footer">
          Already have an account? <Link to="/login">Login here</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;

